/**
 * Operator oversight list pages — cross-tenant Tenants / Hotels / Calls
 * directories. Each is URL-driven (filters/page in search params, wired by the
 * router) and renders ONLY canonical operator data from `@hostesa/api-client`
 * (gated by `admin:cross_tenant_read`). The call directory is diagnostics only:
 * no transcript or guest content.
 */

import {
  type OperatorCall,
  type OperatorCallsFilters,
  type OperatorHotelsFilters,
  type OperatorTenantsFilters,
  type TenantPlan,
  type TenantStatus,
  useOperatorCalls,
  useOperatorHotels,
  useOperatorTenants,
} from '@hostesa/api-client';
import { useFormatters, useT } from '@hostesa/i18n';
import {
  Button,
  LanguagePill,
  LedgerTable,
  type PillTone,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  StatusPill,
  StatusTabs,
} from '@hostesa/ui';
import { Building2, Phone, Plus, Users } from 'lucide-react';
import { type ReactNode, useState } from 'react';
import { OversightFrame } from './list-scaffold';

/** Compact real-data header figure (mirrors the Users page metrics). */
function HeaderMetric({ testId, label, value }: { testId: string; label: string; value: number }) {
  return (
    <div className="flex flex-col gap-1" data-testid={testId}>
      <span className="font-[var(--font-mono)] text-[10px] font-medium uppercase tracking-widest text-[var(--color-stone-mid)]">
        {label}
      </span>
      <span
        data-testid={`${testId}-value`}
        className="font-[var(--font-display)] text-2xl leading-none text-[var(--color-charcoal)]"
      >
        {value}
      </span>
    </div>
  );
}

const VIEWER_TZ = Intl.DateTimeFormat().resolvedOptions().timeZone;

const TENANT_STATUSES: readonly TenantStatus[] = ['active', 'suspended', 'cancelled'];
const TENANT_PLANS: readonly TenantPlan[] = ['starter', 'growth', 'pro'];
const HOTEL_STATUSES = ['draft', 'active', 'suspended'] as const;
const CALL_STATUSES = [
  'created',
  'ringing',
  'answered',
  'ai_active',
  'handoff_requested',
  'human_active',
  'completed',
  'failed',
] as const;

// Group the real call statuses into the three diagnostics tabs.
const CALL_STATE_GROUPS = {
  inProgress: ['created', 'ringing', 'answered', 'ai_active', 'handoff_requested', 'human_active'],
  completed: ['completed'],
  failed: ['failed'],
} as const;
type CallStateGroup = keyof typeof CALL_STATE_GROUPS;

function tenantTone(status: TenantStatus): PillTone {
  if (status === 'active') return 'success';
  if (status === 'suspended') return 'alert';
  return 'neutral';
}
function hotelTone(status: 'draft' | 'active' | 'suspended'): PillTone {
  if (status === 'active') return 'success';
  if (status === 'suspended') return 'alert';
  return 'neutral';
}
function callTone(status: (typeof CALL_STATUSES)[number]): PillTone {
  if (status === 'completed') return 'success';
  if (status === 'failed') return 'alert';
  if (status === 'handoff_requested' || status === 'human_active') return 'accent';
  return 'neutral';
}
function toSeconds(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}
function langCode(v: unknown): string | null {
  return typeof v === 'string' && v.trim() ? v.trim().toUpperCase() : null;
}

// ───────────────────────────────────────────────────────────────────────────
// Tenants
// ───────────────────────────────────────────────────────────────────────────

export interface TenantsPageProps {
  filters?: OperatorTenantsFilters;
  onFiltersChange?: (next: OperatorTenantsFilters) => void;
  /** Optional click handler for a tenant row (router wires navigation). */
  onSelectTenant?: (tenantId: string) => void;
  /** Navigate to the partner-registration flow (router wires navigation). */
  onRegister?: () => void;
}

export function TenantsPage({
  filters = {},
  onFiltersChange,
  onSelectTenant,
  onRegister,
}: TenantsPageProps = {}) {
  const { t } = useT('backoffice');
  const apply = onFiltersChange ?? (() => {});
  const limit = filters.limit ?? 50;
  const offset = filters.offset ?? 0;

  const query = useOperatorTenants({ ...filters, limit, offset });
  const rows = query.data?.items ?? [];
  const total = query.data?.total ?? 0;
  const filterActive = Boolean(filters.q || filters.status || filters.plan);
  const patch = (next: Partial<OperatorTenantsFilters>) =>
    apply({ ...filters, ...next, offset: 0 });

  return (
    <OversightFrame
      testId="tenants-page"
      eyebrow={t('navGroups.oversight')}
      title={t('pages.tenants.title')}
      description={t('pages.tenants.description')}
      headerTrailing={
        <div className="flex shrink-0 items-end gap-6" data-testid="tenants-page-metrics">
          <HeaderMetric
            testId="tenants-metric-total"
            label={t('oversight.tenants.metrics.total')}
            value={total}
          />
          {onRegister && (
            <Button type="button" size="sm" data-testid="tenants-register-cta" onClick={onRegister}>
              <Plus className="size-4" />
              {t('oversight.tenants.registerCta')}
            </Button>
          )}
        </div>
      }
      searchValue={filters.q ?? ''}
      onSearchChange={(v) => patch({ q: v || undefined })}
      searchPlaceholder={t('oversight.tenants.searchPlaceholder')}
      filterActive={filterActive}
      onClear={() => apply({ limit })}
      isLoading={query.isLoading}
      isError={query.isError}
      isEmpty={rows.length === 0}
      emptyIcon={<Users />}
      emptyTitle={t('oversight.tenants.empty.title')}
      emptyDescription={t('oversight.tenants.empty.description')}
      emptyFilteredTitle={t('oversight.tenants.emptyFiltered.title')}
      emptyFilteredDescription={t('oversight.tenants.emptyFiltered.description')}
      total={total}
      limit={limit}
      offset={offset}
      onPageChange={({ offset: o, limit: l }) => apply({ ...filters, offset: o, limit: l })}
    >
      <StatusTabs
        data-testid="tenants-tabs"
        value={filters.status ?? 'all'}
        onChange={(id) => patch({ status: id === 'all' ? undefined : (id as TenantStatus) })}
        tabs={[
          { id: 'all', label: t('oversight.tabsAll') },
          ...TENANT_STATUSES.map((s) => ({
            id: s,
            label: t(`oversight.tenants.status.${s}`),
          })),
        ]}
        className="mb-3"
      />
      <LedgerTable
        data-testid="tenants-table"
        rows={rows}
        getRowKey={(tn) => tn.id}
        onRowClick={onSelectTenant ? (tn) => onSelectTenant(tn.id) : undefined}
        columns={[
          {
            id: 'tenant',
            header: t('oversight.columns.tenant'),
            width: 'minmax(0,2fr)',
            cell: (tn) => (
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate font-medium text-[var(--color-charcoal)]">{tn.name}</span>
                <span className="truncate font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
                  {tn.slug}
                </span>
              </div>
            ),
          },
          {
            id: 'hotels',
            header: t('oversight.columns.hotels'),
            align: 'right',
            width: '80px',
            cell: (tn) => tn.hotelsCount,
          },
          {
            id: 'plan',
            header: t('oversight.columns.plan'),
            width: '90px',
            cell: (tn) => t(`oversight.tenants.plan.${tn.plan}`),
          },
          {
            id: 'calls24h',
            header: t('oversight.columns.calls24h'),
            align: 'right',
            width: '90px',
            cell: (tn) => tn.calls24h,
          },
          {
            id: 'status',
            header: t('oversight.columns.status'),
            width: '120px',
            cell: (tn) => (
              <StatusPill
                dot
                tone={tenantTone(tn.status)}
                label={t(`oversight.tenants.status.${tn.status}`)}
              />
            ),
          },
        ]}
      />
    </OversightFrame>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Hotels
// ───────────────────────────────────────────────────────────────────────────

export interface HotelsPageProps {
  filters?: OperatorHotelsFilters;
  onFiltersChange?: (next: OperatorHotelsFilters) => void;
  /** Optional click handler for a hotel row (router wires navigation). */
  onSelectHotel?: (hotelId: string) => void;
}

export function HotelsPage({ filters = {}, onFiltersChange, onSelectHotel }: HotelsPageProps = {}) {
  const { t } = useT('backoffice');
  const apply = onFiltersChange ?? (() => {});
  const limit = filters.limit ?? 50;
  const offset = filters.offset ?? 0;

  // The backend hotels endpoint has no status query param, so `status` is applied
  // client-side over the loaded page (not sent to the server, so it never claims a
  // filter that does not exist). It is omitted from the query input below.
  const { status: statusFilter, ...serverFilters } = filters;
  const query = useOperatorHotels({ ...serverFilters, limit, offset });
  const allRows = query.data?.items ?? [];
  const rows = statusFilter ? allRows.filter((h) => h.status === statusFilter) : allRows;
  const total = query.data?.total ?? 0;
  const filterActive = Boolean(filters.q || filters.tenantId || filters.status);
  const patch = (next: Partial<OperatorHotelsFilters>) => apply({ ...filters, ...next, offset: 0 });

  return (
    <OversightFrame
      testId="hotels-page"
      eyebrow={t('navGroups.oversight')}
      title={t('pages.hotels.title')}
      description={t('pages.hotels.description')}
      headerTrailing={
        <div className="flex shrink-0 gap-6" data-testid="hotels-page-metrics">
          <HeaderMetric
            testId="hotels-metric-total"
            label={t('oversight.hotels.metrics.total')}
            value={total}
          />
        </div>
      }
      searchValue={filters.q ?? ''}
      onSearchChange={(v) => patch({ q: v || undefined })}
      searchPlaceholder={t('oversight.hotels.searchPlaceholder')}
      filterActive={filterActive}
      onClear={() => apply({ limit })}
      isLoading={query.isLoading}
      isError={query.isError}
      isEmpty={rows.length === 0}
      emptyIcon={<Building2 />}
      emptyTitle={t('oversight.hotels.empty.title')}
      emptyDescription={t('oversight.hotels.empty.description')}
      emptyFilteredTitle={t('oversight.hotels.emptyFiltered.title')}
      emptyFilteredDescription={t('oversight.hotels.emptyFiltered.description')}
      total={total}
      limit={limit}
      offset={offset}
      onPageChange={({ offset: o, limit: l }) => apply({ ...filters, offset: o, limit: l })}
    >
      <StatusTabs
        data-testid="hotels-tabs"
        value={filters.status ?? 'all'}
        onChange={(id) =>
          patch({ status: id === 'all' ? undefined : (id as (typeof HOTEL_STATUSES)[number]) })
        }
        tabs={[
          { id: 'all', label: t('oversight.tabsAll') },
          ...HOTEL_STATUSES.map((s) => ({
            id: s,
            label: t(`oversight.hotels.status.${s}`),
          })),
        ]}
        className="mb-3"
      />
      <LedgerTable
        data-testid="hotels-table"
        rows={rows}
        getRowKey={(h) => h.id}
        onRowClick={onSelectHotel ? (h) => onSelectHotel(h.id) : undefined}
        columns={[
          {
            id: 'hotel',
            header: t('oversight.columns.hotel'),
            width: 'minmax(0,2fr)',
            cell: (h) => (
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate font-medium text-[var(--color-charcoal)]">{h.name}</span>
                <span className="truncate font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
                  {h.slug}
                </span>
              </div>
            ),
          },
          {
            id: 'tenant',
            header: t('oversight.columns.tenant'),
            width: 'minmax(0,2fr)',
            cell: (h) => (
              <div className="flex min-w-0 flex-col gap-0.5">
                <span className="truncate text-[var(--color-stone-700)]">
                  {h.tenantName ?? t('oversight.hotels.unknownPartner')}
                </span>
                {h.tenantSlug && (
                  <span className="truncate font-[var(--font-mono)] text-[11px] text-[var(--color-stone-mid)]">
                    {h.tenantSlug}
                  </span>
                )}
              </div>
            ),
          },
          {
            id: 'calls24h',
            header: t('oversight.columns.calls24h'),
            align: 'right',
            width: '90px',
            cell: (h) => h.calls24h,
          },
          {
            // LINES: REAL active/total phone-line counts (`phone_numbers` table).
            id: 'lines',
            header: t('oversight.columns.lines'),
            align: 'right',
            width: '80px',
            cell: (h) => (
              <span
                data-testid={`hotels-lines-${h.id}`}
                className="font-[var(--font-mono)] text-[12.5px] text-[var(--color-stone-700)]"
              >
                {h.linesTotal === undefined
                  ? t('oversight.notAvailable')
                  : `${h.linesActive ?? 0}/${h.linesTotal}`}
              </span>
            ),
          },
          {
            id: 'status',
            header: t('oversight.columns.status'),
            width: '120px',
            cell: (h) => (
              <StatusPill
                dot
                tone={hotelTone(h.status)}
                label={t(`oversight.hotels.status.${h.status}`)}
              />
            ),
          },
        ]}
      />
    </OversightFrame>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Calls
// ───────────────────────────────────────────────────────────────────────────

export interface CallsPageProps {
  filters?: OperatorCallsFilters;
  onFiltersChange?: (next: OperatorCallsFilters) => void;
  /** Navigate to the full call detail page (router wires navigation). */
  onViewCallDetails?: (callId: string) => void;
}

/** Which diagnostics tab a concrete call status belongs to. */
function callStateGroup(status: OperatorCall['status']): CallStateGroup {
  if ((CALL_STATE_GROUPS.completed as readonly string[]).includes(status)) return 'completed';
  if ((CALL_STATE_GROUPS.failed as readonly string[]).includes(status)) return 'failed';
  return 'inProgress';
}

export function CallsPage({
  filters = {},
  onFiltersChange,
  onViewCallDetails,
}: CallsPageProps = {}) {
  const { t } = useT('backoffice');
  const { formatDateTime, formatDuration } = useFormatters(VIEWER_TZ);
  const apply = onFiltersChange ?? (() => {});
  const limit = filters.limit ?? 50;
  const offset = filters.offset ?? 0;
  const [selected, setSelected] = useState<OperatorCall | null>(null);

  // The backend `status` param takes a single concrete call status. The tabs are
  // coarser diagnostics groups (in-progress / completed / failed), so the active
  // group is held in the URL `status` slot but applied client-side over the loaded
  // page — it is not sent as a server filter (which would not understand a group).
  const activeGroup = filters.status as string | undefined;
  const { status: _status, ...serverFilters } = filters;
  const query = useOperatorCalls({ ...serverFilters, limit, offset });
  const allRows = query.data?.items ?? [];
  const rows =
    activeGroup && activeGroup in CALL_STATE_GROUPS
      ? allRows.filter((c) => callStateGroup(c.status) === activeGroup)
      : allRows;
  const total = query.data?.total ?? 0;
  const filterActive = Boolean(
    filters.q ||
      filters.status ||
      filters.direction ||
      filters.language ||
      filters.tenantId ||
      filters.hotelId,
  );
  const patch = (next: Partial<OperatorCallsFilters>) => apply({ ...filters, ...next, offset: 0 });

  return (
    <OversightFrame
      testId="calls-page"
      eyebrow={t('navGroups.oversight')}
      title={t('pages.calls.title')}
      description={t('pages.calls.description')}
      headerTrailing={
        <div className="flex shrink-0 gap-6" data-testid="calls-page-metrics">
          <HeaderMetric
            testId="calls-metric-total"
            label={t('oversight.calls.metrics.total')}
            value={total}
          />
        </div>
      }
      searchValue={filters.q ?? ''}
      onSearchChange={(v) => patch({ q: v || undefined })}
      searchPlaceholder={t('oversight.calls.searchPlaceholder')}
      filterActive={filterActive}
      onClear={() => apply({ limit })}
      isLoading={query.isLoading}
      isError={query.isError}
      isEmpty={rows.length === 0}
      emptyIcon={<Phone />}
      emptyTitle={t('oversight.calls.empty.title')}
      emptyDescription={t('oversight.calls.empty.description')}
      emptyFilteredTitle={t('oversight.calls.emptyFiltered.title')}
      emptyFilteredDescription={t('oversight.calls.emptyFiltered.description')}
      total={total}
      limit={limit}
      offset={offset}
      onPageChange={({ offset: o, limit: l }) => apply({ ...filters, offset: o, limit: l })}
    >
      <StatusTabs
        data-testid="calls-tabs"
        value={activeGroup && activeGroup in CALL_STATE_GROUPS ? activeGroup : 'all'}
        onChange={(id) =>
          patch({ status: id === 'all' ? undefined : (id as OperatorCallsFilters['status']) })
        }
        tabs={[
          { id: 'all', label: t('oversight.tabsAll') },
          { id: 'inProgress', label: t('oversight.callStateGroups.inProgress') },
          { id: 'completed', label: t('oversight.callStateGroups.completed') },
          { id: 'failed', label: t('oversight.callStateGroups.failed') },
        ]}
        className="mb-3"
      />
      <LedgerTable
        data-testid="calls-table"
        rows={rows}
        getRowKey={(c) => c.id}
        onRowClick={(c) => setSelected(c)}
        columns={[
          {
            id: 'callId',
            header: t('oversight.columns.callId'),
            width: '110px',
            cell: (c) => (
              <span className="font-[var(--font-mono)] text-[var(--color-stone-700)]">
                {c.id.slice(0, 8)}
              </span>
            ),
          },
          {
            id: 'hotel',
            header: t('oversight.columns.hotel'),
            width: 'minmax(0,2fr)',
            cell: (c) => c.hotelSlug ?? t('oversight.notAvailable'),
          },
          {
            id: 'lang',
            header: t('oversight.columns.lang'),
            width: '70px',
            cell: (c) => {
              const lang = langCode(c.language);
              return lang ? <LanguagePill code={lang} /> : t('oversight.notAvailable');
            },
          },
          {
            id: 'state',
            header: t('oversight.columns.state'),
            width: '150px',
            cell: (c) => (
              <StatusPill
                dot
                tone={callTone(c.status)}
                label={t(`oversight.calls.status.${c.status}`)}
              />
            ),
          },
          {
            id: 'duration',
            header: t('oversight.columns.duration'),
            align: 'right',
            width: '80px',
            cell: (c) => {
              const dur = toSeconds(c.durationSeconds);
              return dur !== null ? formatDuration(dur) : t('oversight.notAvailable');
            },
          },
        ]}
      />

      <CallDetailsDrawer
        call={selected}
        onClose={() => setSelected(null)}
        onViewFull={onViewCallDetails}
        formatDateTime={formatDateTime}
        formatDuration={formatDuration}
      />
    </OversightFrame>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// Call details drawer — renders the already-loaded row fields (no fetch, no new
// endpoint, no transcript/guest content). Diagnostics metadata only.
// ───────────────────────────────────────────────────────────────────────────

interface CallDetailsDrawerProps {
  call: OperatorCall | null;
  onClose: () => void;
  /** Navigate to the full call detail page (keeps the quick-view drawer). */
  onViewFull?: (callId: string) => void;
  formatDateTime: (value: string) => string;
  formatDuration: (totalSeconds: number) => string;
}

function CallDetailsDrawer({
  call,
  onClose,
  onViewFull,
  formatDateTime,
  formatDuration,
}: CallDetailsDrawerProps) {
  const { t } = useT('backoffice');
  const dur = call ? toSeconds(call.durationSeconds) : null;
  const lang = call ? langCode(call.language) : null;

  return (
    <Sheet open={call !== null} onOpenChange={(open) => (open ? undefined : onClose())}>
      <SheetContent data-testid="call-details-drawer">
        <SheetHeader>
          <SheetTitle>{t('oversight.callDetail.title')}</SheetTitle>
          <SheetDescription className="font-[var(--font-mono)] text-[11px]">
            {call?.id}
          </SheetDescription>
        </SheetHeader>
        {call && (
          <div className="flex flex-col px-6 pb-6">
            <DetailRow label={t('oversight.callDetail.hotel')} value={call.hotelSlug} />
            <DetailRow label={t('oversight.callDetail.tenant')} value={call.tenantSlug} />
            <DetailRow
              label={t('oversight.callDetail.direction')}
              value={t(`oversight.calls.direction.${call.direction}`)}
            />
            <DetailRow
              label={t('oversight.callDetail.language')}
              value={lang ?? t('oversight.notAvailable')}
            />
            <DetailRow
              label={t('oversight.callDetail.status')}
              value={
                <StatusPill
                  dot
                  tone={callTone(call.status)}
                  label={t(`oversight.calls.status.${call.status}`)}
                />
              }
            />
            <DetailRow
              label={t('oversight.callDetail.startedAt')}
              value={call.startedAt ? formatDateTime(call.startedAt) : t('oversight.notAvailable')}
            />
            <DetailRow
              label={t('oversight.callDetail.duration')}
              value={dur !== null ? formatDuration(dur) : t('oversight.notAvailable')}
            />
            {onViewFull && (
              <Button
                type="button"
                variant="secondary"
                size="sm"
                data-testid="call-details-view-full"
                className="mt-4 self-start"
                onClick={() => onViewFull(call.id)}
              >
                {t('oversight.callDetail.viewFull')}
              </Button>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function DetailRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 border-t border-[var(--color-line-2)] py-2.5 text-[13.5px] first:border-t-0">
      <span className="text-[var(--color-stone-mid)]">{label}</span>
      <span className="text-right font-medium text-[var(--color-charcoal)]">
        {value ?? <span className="text-[var(--color-stone-mid)]">—</span>}
      </span>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
// URL search parsing (router validateSearch). Only provided, valid params are
// kept — defaults (limit 50 / offset 0) are owned by the page + backend, so a
// bare URL stays clean.
// ───────────────────────────────────────────────────────────────────────────

const str = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
const num = (v: unknown) => {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : Number.NaN;
  return Number.isFinite(n) ? n : undefined;
};
const TENANT_STATUS_SET = new Set<string>(TENANT_STATUSES);
const TENANT_PLAN_SET = new Set<string>(TENANT_PLANS);
const HOTEL_STATUS_SET = new Set<string>(HOTEL_STATUSES);
// Calls are filtered by coarse diagnostics group (not a single backend status).
const CALL_GROUP_SET = new Set<string>(Object.keys(CALL_STATE_GROUPS));

function pageParams<T extends { limit?: number; offset?: number }>(
  raw: Record<string, unknown>,
  out: T,
): T {
  const limit = num(raw.limit);
  const offset = num(raw.offset);
  if (limit !== undefined) out.limit = Math.min(Math.max(limit, 1), 200);
  if (offset !== undefined && offset > 0) out.offset = offset;
  return out;
}

export function parseTenantsSearch(raw: Record<string, unknown>): OperatorTenantsFilters {
  const out: OperatorTenantsFilters = {};
  if (str(raw.q)) out.q = str(raw.q);
  const status = str(raw.status);
  if (status && TENANT_STATUS_SET.has(status)) out.status = status as TenantStatus;
  const plan = str(raw.plan);
  if (plan && TENANT_PLAN_SET.has(plan)) out.plan = plan as TenantPlan;
  return pageParams(raw, out);
}

export function parseHotelsSearch(raw: Record<string, unknown>): OperatorHotelsFilters {
  const out: OperatorHotelsFilters = {};
  if (str(raw.q)) out.q = str(raw.q);
  if (str(raw.tenantId)) out.tenantId = str(raw.tenantId);
  const status = str(raw.status);
  if (status && HOTEL_STATUS_SET.has(status))
    out.status = status as OperatorHotelsFilters['status'];
  return pageParams(raw, out);
}

export function parseCallsSearch(raw: Record<string, unknown>): OperatorCallsFilters {
  const out: OperatorCallsFilters = {};
  if (str(raw.q)) out.q = str(raw.q);
  // `status` carries the diagnostics tab group (in-progress / completed / failed),
  // applied client-side; it is not a single backend call status.
  const status = str(raw.status);
  if (status && CALL_GROUP_SET.has(status)) out.status = status as OperatorCallsFilters['status'];
  const direction = str(raw.direction);
  if (direction === 'inbound' || direction === 'outbound') out.direction = direction;
  if (str(raw.language)) out.language = str(raw.language);
  if (str(raw.tenantId)) out.tenantId = str(raw.tenantId);
  if (str(raw.hotelId)) out.hotelId = str(raw.hotelId);
  if (str(raw.from)) out.from = str(raw.from);
  if (str(raw.to)) out.to = str(raw.to);
  return pageParams(raw, out);
}
